import type { ShoppingCartDTO, CartItemDTO } from '@t/api.types';
import type { Plan, CategoryType } from '@t/productOffering.types';

/** Check if a specific offering is present in the cart. */
export const isOfferingInCart = (cart: ShoppingCartDTO | null, offeringId: string): boolean =>
  cart?.cartItem?.some((item) => item.productOffering?.id === offeringId) ?? false;

/** Find the selected plan ID from a list of plans for a given category. */
export const getSelectedPlanId = (cart: ShoppingCartDTO | null, plans: Plan[]): string | null => {
  const cartItem = cart?.cartItem?.find((item) => plans.some((p) => p.planId === item.productOffering?.id));
  return cartItem?.productOffering?.id ?? null;
};

/** Check if a category has a plan selected in the cart. */
export const isCategoryInCart = (cart: ShoppingCartDTO | null, plans: Plan[]): boolean =>
  getSelectedPlanId(cart, plans) !== null;

/** Infer category from a cart item's offering name. */
export const getCategoryFromItemName = (name: string | undefined): CategoryType | null => {
  const lower = (name ?? '').toLowerCase();
  if (lower.includes('internet') || lower.includes('wifi')) return 'Internet';
  if (lower.includes('tv') || lower.includes('video')) return 'Video';
  if (lower.includes('phone')) return 'Phone';
  return null;
};

/** Check if any Internet plan is in the cart (by item name). */
export const hasInternetInCart = (cart: ShoppingCartDTO | null): boolean =>
  cart?.cartItem?.some((item) => getCategoryFromItemName(item.productOffering?.name) === 'Internet') ?? false;

/** Check if any Phone plan is in the cart (by item name). */
export const hasPhoneInCart = (cart: ShoppingCartDTO | null): boolean =>
  cart?.cartItem?.some((item) => getCategoryFromItemName(item.productOffering?.name) === 'Phone') ?? false;

const hasInstallationTypeCharacteristic = (item: CartItemDTO): boolean => {
  const offering = item.productOffering;
  const chars = offering?.productOfferingCharacteristic ?? [];
  const specValueUse = offering?.prodSpecCharValueUse ?? [];
  const found = chars.some((c) => c.name === 'installationType') || specValueUse.some((c) => c.name === 'installationType');
  return found;
};

/** Returns true when installation step should be shown:
 *  - any Telco internet plan is in the cart (name-based, backward-compat), OR
 *  - any item (top-level or child) declares an `installationType` characteristic (EV flow). */
export const hasInstallationRequiredInCart = (cart: ShoppingCartDTO | null): boolean => {
  if (hasInternetInCart(cart)) return true;
  const allItems = cart?.cartItem ?? [];
  return allItems.some(
    (item) => hasInstallationTypeCharacteristic(item) || (item.childItems ?? []).some(hasInstallationTypeCharacteristic)
  );
};

/**
 * Determine whether deleting an item requires removing all items.
 * - 'internet-dependency': Internet item with other items in the cart — needs warning + empty cart.
 * - 'internet-only': Internet is the only item — empty the cart silently so the caller can reset/navigate.
 * - null: safe to delete individually.
 */
export const getDeleteConstraint = (
  item: CartItemDTO,
  allItems: CartItemDTO[]
): 'internet-dependency' | 'internet-only' | null => {
  const category = getCategoryFromItemName(item.productOffering?.name);
  if (category !== 'Internet') return null;
  return allItems.length > 1 ? 'internet-dependency' : 'internet-only';
};
